import { useState, type ReactNode } from 'react';
import { errorCode } from '../lib/errors';
import { LoadFailed } from '../components/QueryState';
import { createFileRoute, Link, redirect, useCanGoBack, useRouter } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLE_LABELS, displayName, initials } from '@promanage/contracts/person-display';
import { formatRef, REF_PREFIX, type RefEntity } from '@promanage/core';
import { Button, Chip, Input, Panel, PanelHeader, Ref, cn } from '@promanage/ui';
import { RecordNotFound } from '../components/RecordNotFound';
import { TopBar } from '../components/TopBar';
import { RecordActions } from '../components/RecordActions';
import { RelatedRecordRow } from '../components/RelatedRecordRow';
import { PanelHelp } from '../components/PanelHelp';
import { RecordSkeleton } from '../components/RecordSkeleton';
import { RecordIdentity } from '../components/RecordIdentity';
import { RecordTabs } from '../components/RecordTabs';
import { RecordCommunications } from '../features/inbox/RecordCommunications';
import { readTabSearch } from '../components/tabSearch';
import { RecordFiles } from '../features/reports/RecordFiles';
import { EmailStopNotice } from '../features/correspondence/EmailStops';
import { Icon } from '../components/Icon';
import { AttachRoleDialog } from '../features/people/AttachRole';
import { CommsPreferencePanel } from '../features/people/CommsPreference';
import { PersonPortal } from '../features/people/PersonPortal';
import type { PersonDetail, PersonRelationship, PersonRole } from '../features/people/types';
import {
  contactMethods,
  currentRolesBySubject,
  formatDate,
  formatDuration,
  formatSpan,
  isCurrent,
  relationshipLabel,
  roleHistory,
  roleSummary,
  roleTone,
  toIsoDate,
} from '../features/people/detail';
import { resolvePersonRef } from '../features/people/personRef';
import { trpc } from '../lib/trpc';

/**
 * The person record, addressed by its display ref: /people/PE000003.
 *
 * The URL deliberately carries the ref rather than the uuid, so someone reading a code off
 * a bond form or a statement can type it straight into the address bar. That makes the
 * segment user input: `beforeLoad` normalises whatever was typed to the canonical form, and
 * the component answers every remaining wrong shape explicitly.
 *
 * `.index.tsx`, NOT `.tsx`, and the suffix is load-bearing. Under flat file routing a
 * `people.$ref.tsx` sitting beside `people.$ref.edit.tsx` is not its sibling — it is its
 * PARENT, and a parent renders its child through an `<Outlet />`. This page has none and
 * should not have one: an edit form appended underneath a read-only record is not a page
 * anyone wants. So `/people/PE000003/edit` matched the edit route correctly and then painted
 * this component over the top of it, and Edit looked like it simply did not work.
 *
 * As an index route both are leaves under a component-less parent, and each renders alone.
 * `routing.test.ts` pins the general rule rather than this one instance.
 */
export const Route = createFileRoute('/people/$ref/')({
  /*
   * Which section is open, in the URL. Shared with the owner, property and tenancy pages — see
   * `readTabSearch` for why it is its own import-free module.
   */
  validateSearch: readTabSearch,

  beforeLoad: ({ params, search }) => {
    const resolved = resolvePersonRef(params.ref);
    // "pe3" and "PE-3" are the same record as PE000003 — redirect rather than 404, so a
    // hand-typed ref lands on the record AND the address bar teaches the canonical form.
    // Safe from looping: resolvePersonRef(canonical) === canonical, which is under test.
    if (resolved.status === 'ok' && resolved.canonical !== params.ref) {
      throw redirect({
        to: '/people/$ref',
        params: { ref: resolved.canonical },
        // Carried over: normalising the ref must not close the tab somebody linked to.
        search,
        replace: true,
      });
    }
  },
  component: PersonDetailPage,
});

function PersonDetailPage() {
  const { ref } = Route.useParams();
  const resolved = resolvePersonRef(ref);

  // Before the early returns — hooks cannot be called conditionally. Disabled rather than
  // skipped when the segment is not a person ref, so the component below still owns the
  // "which kind of wrong is this" branching.
  const person = useQuery(
    trpc.people.get.queryOptions(
      { refSeq: resolved.status === 'ok' ? resolved.sequence : 1 },
      {
        meta: { errorTitle: 'Could not load that person' },
        enabled: resolved.status === 'ok',
        staleTime: 10_000,
        retry: false,
      },
    ),
  );

  if (resolved.status === 'malformed') {
    return (
      <Unresolved title="Not a record reference">
        <p>
          <code className="font-mono text-ink">{ref}</code> is not a record reference. Person
          records look like <code className="font-mono text-ink">PE000003</code> — two letters for
          the record type, then its number.
        </p>
      </Unresolved>
    );
  }

  if (resolved.status === 'other-entity') {
    return (
      <Unresolved title={`${resolved.canonical} is not a person`}>
        <p>
          <code className="font-mono text-ink">{resolved.canonical}</code> is{' '}
          {describeEntity(resolved.entity)} reference, not a person. Person references start with{' '}
          <code className="font-mono text-ink">{REF_PREFIX.person}</code>.
        </p>
      </Unresolved>
    );
  }

  if (person.isPending) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Person" subtitle={resolved.canonical} actions={<HeaderActions />} />
        <RecordSkeleton />
      </div>
    );
  }

  /*
   * A MISSING RECORD AND A FAILED LOAD ARE DIFFERENT ANSWERS. Not found is this page's own
   * answer, and the notices stay out of it. Anything else — the server erroring, the connection
   * dropping — is a load that failed, which the notices have already reported, and saying "No
   * such …" about it would tell the operator a record does not exist when it does.
   */
  if (person.error && errorCode(person.error) !== 'NOT_FOUND') {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Person" subtitle={resolved.canonical} actions={<HeaderActions />} />
        <LoadFailed retry={() => void person.refetch()} retrying={person.isFetching} />
      </div>
    );
  }

  if (person.error || !person.data) {
    return (
      <Unresolved title={`No person ${resolved.canonical}`}>
        <p>
          Nothing in this agency uses the reference{' '}
          <code className="font-mono text-ink">{resolved.canonical}</code>. It may belong to a
          different agency — references are numbered per agency, so the same code means different
          records elsewhere.
        </p>
      </Unresolved>
    );
  }

  return <Record person={person.data} />;
}

function Record({ person }: { person: PersonDetail }) {
  const queryClient = useQueryClient();
  const { tab } = Route.useSearch();
  // The clock is read here, at the edge, and passed into the pure helpers — so "2 yrs 1 mo"
  // is testable without freezing time.
  const today = toIsoDate(new Date());
  const groups = currentRolesBySubject(person.roles, today);
  const history = roleHistory(person.roles, today);
  const contacts = contactMethods(person);
  const legalName = `${person.firstName} ${person.lastName}`;
  const [attaching, setAttaching] = useState(false);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: trpc.people.get.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.people.list.queryKey() });
    // The record on the other end of the role now lists this person, or no longer does.
    void queryClient.invalidateQueries({ queryKey: trpc.property.get.queryKey() });
  };

  const setStatus = useMutation(
    trpc.people.setStatus.mutationOptions({
      meta: { errorTitle: 'Could not change that record’s status' },
      onSuccess: refresh,
    }),
  );

  const endRole = useMutation(
    trpc.people.endRole.mutationOptions({
      meta: { errorTitle: 'Could not end that role' },
      onSuccess: refresh,
    }),
  );

  const archived = person.status === 'archived';

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      {/*
       * The bar says what KIND of page this is; the record's own name is the heading in the
       * card below and is not repeated here. The subtitle carries the ref because the bar is
       * sticky — once the identity card has scrolled away it is the only thing on screen
       * saying which record you are looking at.
       */}
      <TopBar
        title="Person"
        subtitle={`${formatRef('person', person.refSeq)} · ${roleSummary(person.roles, today)}`}
        actions={
          <>
            <HeaderActions />
            {/*
             * Archive, never delete. A person with role history cannot go away — the history
             * is what the identity model exists for, and a tenancy agreement naming somebody
             * who no longer exists is worse than a record marked archived.
             */}
            <RecordActions
              pending={setStatus.isPending}
              onSelect={() =>
                setStatus.mutate({
                  personId: person.id,
                  status: archived ? 'active' : 'archived',
                })
              }
              label={archived ? 'Restore' : 'Archive'}
            />
            {/* Primary action, so it sits last and carries the accent. */}
            <Link to="/people/$ref/edit" params={{ ref: formatRef('person', person.refSeq) }}>
              <Button size="sm">Edit</Button>
            </Link>
          </>
        }
      />

      <div className="flex flex-col gap-4">
        {archived ? (
          <div
            role="note"
            className="rounded-md bg-warn-soft px-4 py-2.5 text-[0.8125rem] text-warn ring-1 ring-warn/25"
          >
            <strong className="font-bold">Archived.</strong> This record is kept for history and is
            hidden from the people list by default. Archiving never deletes roles — the history
            below is intact.
          </div>
        ) : null}

        <RecordIdentity
          title={displayName(person)}
          symbol={initials(person)}
          status={
            <Chip tone={archived ? 'neutral' : 'positive'}>{archived ? 'Archived' : 'Active'}</Chip>
          }
          subtitle={
            displayName(person) !== legalName ? (
              <>
                Legal name <span className="text-ink">{legalName}</span>
              </>
            ) : null
          }
          facts={[
            {
              label: 'Person reference',
              value: <Ref entity="person" sequence={person.refSeq} tone="strong" />,
            },
            { label: 'Added', value: formatDate(person.createdAt) },
          ]}
        />

        <RecordTabs
          from="/people/$ref/"
          current={tab ?? 'overview'}
          label="Person sections"
          portal
        />

        {tab === 'communications' ? (
          <RecordCommunications record={{ type: 'person', id: person.id }} />
        ) : tab === 'portal' ? (
          <PersonPortal person={person} />
        ) : tab === 'documents' ? (
          /*
           * FULL WIDTH, not dropped into the overview’s side column. A document row carries a
           * file name, a size, a note and a withdrawal record, and a third-of-a-page column
           * truncates all four — which is the one thing an operator is reading it to check.
           */
          <RecordFiles subject={{ type: 'person', id: person.id }} />
        ) : (
          <>
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="flex flex-col gap-4 lg:col-span-2">
                {/* ── Current roles ─────────────────────────────────────────── */}
                <Panel>
                  <PanelHeader
                    title="Current roles"
                    subtitle="Current roles and linked records"
                    aside={
                      archived ? null : (
                        <Button size="sm" variant="secondary" onClick={() => setAttaching(true)}>
                          Attach to a record
                        </Button>
                      )
                    }
                  />
                  {groups.length === 0 ? (
                    <Blank>
                      <strong className="block text-ink">No current roles</strong>
                      Attach this person to a record, or expand their role history.
                    </Blank>
                  ) : (
                    <div className="flex flex-col gap-4 px-5 pb-5">
                      {groups.map((group) => (
                        <div key={group.subject}>
                          <h3 className="mb-1.5 text-[0.6875rem] font-semibold tracking-[0.06em] text-ink-subtle uppercase">
                            {group.label}
                          </h3>
                          <ul className="flex flex-col gap-1.5">
                            {group.roles.map((role) => (
                              <li
                                key={role.id}
                                className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-surface-sunk px-3 py-2.5"
                              >
                                <Chip tone={roleTone(role.roleType)}>
                                  {ROLE_LABELS[role.roleType]}
                                </Chip>
                                <span className="min-w-0 flex-1 truncate text-[0.875rem] font-semibold">
                                  {role.subjectLabel}
                                  {role.isPrimary ? (
                                    <span className="ml-1.5 text-[0.6875rem] font-semibold tracking-[0.06em] text-ink-subtle uppercase">
                                      Primary
                                    </span>
                                  ) : null}
                                </span>
                                <span className="text-[0.75rem] text-ink-subtle tabular-nums">
                                  since {formatDate(role.startsOn)} ·{' '}
                                  {formatDuration(role.startsOn, null, today)}
                                </span>
                                <EndRoleButton
                                  label={role.subjectLabel}
                                  today={today}
                                  pending={endRole.isPending}
                                  onEnd={(endsOn) => endRole.mutate({ roleId: role.id, endsOn })}
                                />
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </Panel>

                {/* ── History ───────────────────────────────────────────────── */}
                <Panel>
                  <details>
                    <summary className="flex min-h-12 cursor-pointer items-center justify-between gap-3 px-5 py-4 text-[0.875rem] font-semibold">
                      Role history{' '}
                      <span className="text-[0.75rem] font-normal text-ink-muted">
                        {history.length} entries · Expand
                      </span>
                    </summary>
                    {history.length === 0 ? (
                      <Blank>
                        Not attached to anything yet. A role says what this person is to a tenancy
                        or an owner entity, and from when.
                      </Blank>
                    ) : (
                      <ol className="pb-4">
                        {history.map((role, i) => (
                          <HistoryRow
                            key={role.id}
                            role={role}
                            today={today}
                            first={i === 0}
                            last={i === history.length - 1}
                          />
                        ))}
                      </ol>
                    )}
                  </details>
                </Panel>

                {person.notes ? (
                  <Panel>
                    <PanelHeader title="Notes" />
                    <p className="px-5 pb-5 text-[0.875rem] leading-relaxed whitespace-pre-wrap text-ink-muted">
                      {person.notes}
                    </p>
                  </Panel>
                ) : null}
              </div>

              <div className="order-first flex flex-col gap-4 lg:order-none">
                {/* ── Contact ───────────────────────────────────────────────── */}
                <Panel>
                  <PanelHeader title="Contact" />
                  {contacts.length === 0 ? (
                    <Blank>
                      <strong className="block text-ink">No contact details</strong>
                      Edit this person to add an email or phone, or use a related contact.
                    </Blank>
                  ) : (
                    <dl className="flex flex-col gap-3 px-5 pb-5">
                      {contacts.map((method) => (
                        <div key={method.key}>
                          <dt className="text-[0.6875rem] font-semibold tracking-[0.06em] text-ink-subtle uppercase">
                            {method.label}
                          </dt>
                          <dd className="mt-0.5 min-w-0">
                            <a
                              href={method.href}
                              className={cn(
                                'block break-words rounded-sm py-2 text-[0.875rem] underline-offset-4 hover:text-accent hover:underline',
                                method.key === 'email' ? '' : 'tabular-nums',
                              )}
                              title={method.value}
                            >
                              {method.value}
                            </a>
                            {method.key === 'email' ? (
                              <EmailStopNotice address={method.value} />
                            ) : null}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </Panel>

                <CommsPreferencePanel
                  personId={person.id}
                  preference={person.commsPreference}
                  archived={archived}
                />

                {/* ── Related people ────────────────────────────────────────── */}
                <Panel>
                  <PanelHeader
                    title="Related people"
                    subtitle="Family, guarantors and other connections"
                  />
                  {person.relationships.length === 0 ? (
                    <Blank>No linked people.</Blank>
                  ) : (
                    <ul className="flex flex-col gap-2 px-5 pb-5">
                      {person.relationships.map((rel) => (
                        <RelatedPerson key={rel.id} relationship={rel} />
                      ))}
                    </ul>
                  )}
                </Panel>
              </div>
            </div>

            <PanelHelp>
              A role is ended, never deleted — the row is this person’s history, and a tenancy
              agreement naming somebody whose role was deleted is worse than one naming somebody
              whose role ended. Ending a tenancy ends the roles on it.
            </PanelHelp>
          </>
        )}
      </div>

      {attaching ? (
        <AttachRoleDialog
          target={{ mode: 'person', personId: person.id, personName: displayName(person) }}
          onClose={() => setAttaching(false)}
          onDone={() => setAttaching(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * Ending a role asks for the date rather than assuming today.
 *
 * A tenant moved out on a day; the day somebody got round to recording it is a different fact,
 * and the gap between them is rent liability. Today is the default because it is the common
 * case, and it is editable because the common case is not the only one.
 */
function EndRoleButton({
  label,
  today,
  pending,
  onEnd,
}: {
  label: string;
  today: string;
  pending: boolean;
  onEnd: (endsOn: string) => void;
}) {
  const [endsOn, setEndsOn] = useState(today);
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        End
      </Button>
    );
  }

  return (
    <span className="flex items-center gap-2">
      <Input
        type="date"
        aria-label={`Last day of this role on ${label}`}
        value={endsOn}
        onChange={(e) => setEndsOn(e.target.value)}
        className="w-[9.5rem]"
      />
      <Button size="sm" disabled={pending} onClick={() => onEnd(endsOn)}>
        {pending ? 'Ending' : 'Confirm'}
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </span>
  );
}

/**
 * Back, then the list.
 *
 * The two are not the same thing and both earn their place: "Back" returns to wherever you
 * came from — the list with your filters, sort and scroll intact, or another person's record
 * if you arrived through a relationship — while "All people" is a fixed destination that
 * resets those.
 *
 * Back is hidden, not disabled, when there is no in-app history: the ref is addressable, so
 * arriving by pasted URL in a fresh tab is a real entry path, and there a browser back would
 * throw the user out of the app entirely.
 */
function HeaderActions() {
  const router = useRouter();
  const canGoBack = useCanGoBack();

  return (
    <>
      {canGoBack ? (
        <Button variant="secondary" size="sm" onClick={() => router.history.back()}>
          <Icon name="arrow-right" size={14} className="rotate-180" />
          Back
        </Button>
      ) : null}
      <Link to="/people">
        <Button variant="secondary" size="sm">
          All people
        </Button>
      </Link>
    </>
  );
}

/**
 * One entry in the history timeline.
 *
 * The connecting rule is drawn per row and trimmed at the ends — from this row's dot down to
 * its bottom edge, and from the top edge down to the dot — so the segments meet exactly at
 * each dot and the line neither overshoots the first entry nor dangles past the last. A
 * single-entry history draws no line at all, which is the correct picture.
 */
function HistoryRow({
  role,
  today,
  first,
  last,
}: {
  role: PersonRole;
  today: string;
  first: boolean;
  last: boolean;
}) {
  const current = isCurrent(role, today);
  // Dot centre: 1.125rem offset + half of 0.4375rem.
  const centre = '1.34375rem';

  return (
    <li className="relative flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5 pr-5 pl-9">
      <span
        aria-hidden="true"
        className="absolute left-[1.3125rem] w-px bg-rule"
        style={{
          top: first ? centre : 0,
          bottom: last ? `calc(100% - ${centre})` : 0,
        }}
      />
      <span
        aria-hidden="true"
        className={cn(
          'absolute top-[1.125rem] left-[1.125rem] size-[0.4375rem] rounded-pill ring-2 ring-surface',
          current ? 'bg-accent' : 'bg-rule-strong',
        )}
      />

      <Chip tone={current ? roleTone(role.roleType) : 'neutral'}>{ROLE_LABELS[role.roleType]}</Chip>

      <span
        className={cn(
          'min-w-0 flex-1 truncate text-[0.875rem]',
          current ? 'font-semibold' : 'text-ink-muted',
        )}
      >
        {role.subjectLabel}
      </span>

      <span className="text-[0.75rem] text-ink-subtle tabular-nums">
        {formatSpan(role)}
        {/* An ended role is measured against its end date, not today. */}
        <span className="hidden sm:inline">
          {' '}
          · {formatDuration(role.startsOn, role.endsOn, today)}
        </span>
      </span>
    </li>
  );
}

/**
 * A linked person.
 *
 * Links through only when the other record resolves; a relationship pointing at something
 * missing still states the connection rather than rendering a dead link.
 */
function RelatedPerson({ relationship }: { relationship: PersonRelationship }) {
  const name = displayName({
    firstName: relationship.otherFirstName,
    lastName: relationship.otherLastName,
    preferredName: relationship.otherPreferredName,
  });

  return (
    <RelatedRecordRow
      className="rounded-md border-0 bg-surface-sunk px-3"
      identity={
        <Link
          to="/people/$ref"
          params={{ ref: formatRef('person', relationship.otherPersonRefSeq) }}
        >
          {name}
        </Link>
      }
      reference={<Ref entity="person" sequence={relationship.otherPersonRefSeq} />}
      badges={
        <Chip tone="neutral">{relationshipLabel(relationship.kind, relationship.isFrom)}</Chip>
      }
    >
      {relationship.note ?? null}
    </RelatedRecordRow>
  );
}

/** Panel-level empty state. Says what would fill it, not just that it is empty. */
function Blank({ children }: { children: React.ReactNode }) {
  return <p className="px-5 pb-5 text-[0.8125rem] text-ink-muted">{children}</p>;
}

/**
 * The URL did not resolve to a person.
 *
 * Deliberately three different messages rather than one 404: a ref for another entity, a
 * typo, and a number this agency has not issued are three different mistakes, and only the
 * first two are the user's to fix.
 */
function Unresolved({ title, children }: { title: string; children: ReactNode }) {
  return (
    <RecordNotFound
      record="Person"
      title={title}
      back={{ label: 'Back to people', link: { to: '/people' } }}
      actions={<HeaderActions />}
    >
      {children}
    </RecordNotFound>
  );
}

const ENTITY_ARTICLE: Partial<Record<RefEntity, string>> = {
  owner: 'an owner',
  invoice: 'an invoice',
  inspection: 'an inspection',
};

/** "a tenancy" / "an owner" — the article matters when the sentence is read aloud. */
function describeEntity(entity: RefEntity): string {
  return ENTITY_ARTICLE[entity] ?? `a ${entity}`;
}
